import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { genders } from "../../../../../utils/Constants";
import { getCountriesRequest } from "../../../../../store/Lookups/actions";
import {
  getEventPayrollStatsRequest,
  getEventPayrollsRequest,
} from "../../../../../store/Payroll/actions";
import useDebounce from "../../../../../hooks/useDebounce";
import { PAYROLL_DEFAULT_PAGE_SIZE, PAYROLL_PAYMENT_STATUSES } from "../constants/constants";

const EMPTY_FILTERS = {
  gender: null,
  role: null,
  nationality: null,
  removedStatus: null,
  rewardType: null,
  paymentStatus: null,
};

const SEARCH_DEBOUNCE_MS = 450;

const getFileNameFromUrl = (url) => {
  if (!url) return "";
  try {
    const { pathname } = new URL(url);
    return decodeURIComponent(pathname.split("/").pop() || "");
  } catch {
    return String(url).split("/").pop() || "";
  }
};

const useEventPayroll = ({
  event,
  portal = "admin",
  sharedMessages = {},
  labels = {},
  enabled = true,
} = {}) => {
  const dispatch = useDispatch();
  const eventId = event?.id;

  const { list, meta, stats } = useSelector((state) => state.payroll);
  const countries = useSelector((state) => state.lookups.countries);
  const lang = useSelector((state) => state.locale.lang);

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAYROLL_DEFAULT_PAGE_SIZE);
  const [selectedIds, setSelectedIds] = useState([]);

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    if (!enabled) return;
    dispatch(getCountriesRequest({ page: -1 }));
  }, [dispatch, lang, enabled]);

  useEffect(() => {
    if (!enabled || !eventId) return;
    dispatch(getEventPayrollStatsRequest({ portal, eventId }));
  }, [dispatch, portal, eventId, lang, enabled]);

  const genderOptions = useMemo(
    () => [
      { id: 1, name: sharedMessages.male, value: genders.male },
      { id: 2, name: sharedMessages.female, value: genders.female },
    ],
    [sharedMessages.female, sharedMessages.male],
  );

  const roleOptions = useMemo(() => {
    const seen = new Map();
    (event?.event_roles || []).forEach((eventRole) => {
      const role = eventRole?.role;
      if (role?.id && !seen.has(role.id)) {
        seen.set(role.id, { id: role.id, name: role.name, value: role.id });
      }
    });
    return Array.from(seen.values());
  }, [event?.event_roles]);

  const nationalityOptions = useMemo(
    () =>
      (countries || []).map((country) => ({
        id: country.id,
        name: country.name,
        value: country.id,
      })),
    [countries],
  );

  const removedStatusOptions = useMemo(
    () => [
      { id: 1, name: labels.filters?.assigned, value: "approved" },
      { id: 2, name: labels.filters?.removed, value: "removed" },
    ],
    [labels.filters?.assigned, labels.filters?.removed],
  );

  const rewardTypeOptions = useMemo(
    () => [
      { id: 1, name: labels.filters?.reward, value: "reward" },
      {
        id: 2,
        name: labels.filters?.dedication,
        value: "manual_deduction,automatic_deduction",
      },
    ],
    [labels.filters?.dedication, labels.filters?.reward],
  );

  const paymentStatusOptions = useMemo(
    () => [
      { id: 1, name: labels.paymentStatuses?.pending, value: false },
      { id: 2, name: labels.paymentStatuses?.paid, value: true },
    ],
    [labels.paymentStatuses?.paid, labels.paymentStatuses?.pending],
  );

  const filterParams = useMemo(
    () => ({
      ...(String(debouncedSearch || "").trim()
        ? { query: String(debouncedSearch).trim() }
        : {}),
      ...(filters.gender?.value ? { gender: filters.gender.value } : {}),
      ...(filters.role?.value ? { role_id: filters.role.value } : {}),
      ...(filters.nationality?.value
        ? { nationality_id: filters.nationality.value }
        : {}),
      ...(filters.removedStatus?.value
        ? { event_request_status: filters.removedStatus.value }
        : {}),
      ...(filters.rewardType?.value
        ? { adjustment_type: filters.rewardType.value }
        : {}),
      ...(typeof filters.paymentStatus?.value === "boolean"
        ? { paid: filters.paymentStatus.value }
        : {}),
    }),
    [debouncedSearch, filters],
  );

  const listParams = useMemo(
    () => ({
      page,
      items: pageSize,
      ...filterParams,
    }),
    [filterParams, page, pageSize],
  );

  useEffect(() => {
    if (!enabled || !eventId) return;
    dispatch(getEventPayrollsRequest({ portal, eventId, params: listParams }));
  }, [dispatch, portal, eventId, listParams, lang, enabled]);

  const refresh = useCallback(() => {
    if (!eventId) return;
    dispatch(getEventPayrollsRequest({ portal, eventId, params: listParams }));
    dispatch(getEventPayrollStatsRequest({ portal, eventId }));
  }, [dispatch, portal, eventId, listParams]);

  const rows = useMemo(
    () =>
      (list || []).map((row) => ({
        id: row.id,
        name: row.name,
        image: row.image,
        role: row.current_role?.role || null,
        nationality: row.nationality?.name || null,
        finalPayroll: row.total_amount,
        paymentStatus:
          row.payment_status === "paid"
            ? PAYROLL_PAYMENT_STATUSES.paid
            : PAYROLL_PAYMENT_STATUSES.pending,
        paymentProof: row.payment_proof_url
          ? {
              fileName: getFileNameFromUrl(row.payment_proof_url),
              url: row.payment_proof_url,
            }
          : null,
      })),
    [list],
  );

  const hasActiveSearchOrFilter =
    String(search || "").trim().length > 0 || Object.values(filters).some(Boolean);

  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    setPage(1);
    setSelectedIds([]);
  }, []);

  const handleFilterChange = useCallback((patch) => {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(1);
    setSelectedIds([]);
  }, []);

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage);
    setSelectedIds([]);
  }, []);

  const handlePageSizeChange = useCallback((nextPageSize) => {
    setPageSize(nextPageSize);
    setPage(1);
    setSelectedIds([]);
  }, []);

  const handleSelectRow = useCallback((rowId) => {
    setSelectedIds((current) =>
      current.includes(rowId)
        ? current.filter((id) => id !== rowId)
        : [...current, rowId],
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds((current) => {
      const pageIds = rows.map((row) => row.id);
      const allSelected =
        pageIds.length > 0 && pageIds.every((id) => current.includes(id));

      if (allSelected) {
        return current.filter((id) => !pageIds.includes(id));
      }

      return [...new Set([...current, ...pageIds])];
    });
  }, [rows]);

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const total = meta.count;
  const rangeFrom = total === 0 ? 0 : (meta.currentPage - 1) * pageSize + 1;
  const rangeTo = Math.min(meta.currentPage * pageSize, total);

  return {
    search,
    filters,
    page: meta.currentPage,
    pageSize,
    pages: meta.pages,
    rangeFrom,
    rangeTo,
    total,
    rows,
    allRows: rows,
    stats,
    selectedIds,
    genderOptions,
    roleOptions,
    nationalityOptions,
    removedStatusOptions,
    rewardTypeOptions,
    paymentStatusOptions,
    hasActiveSearchOrFilter,
    filterParams,
    onSearchChange: handleSearchChange,
    onFilterChange: handleFilterChange,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    onSelectRow: handleSelectRow,
    onSelectAll: handleSelectAll,
    clearSelection,
    refresh,
  };
};

export default useEventPayroll;
